import { useQuery } from '@tanstack/react-query';
import { resolutionToScale } from '@agissc/ktag-map';

interface WmsLegendItem {
  blob: Blob;
  label: string;
}

interface WmsLegendRule {
  name: string;
  title: string;
  symbolizers?: string[];
}

const wmsApiUrl =
  'https://wms.geo.ag.ch/public/?service=wms&version=1.3.0&request=GetLegendGraphic';

const fetchWmsLegendItems = async (baseurl: string, rules: WmsLegendRule[]) => {
  const url = `${baseurl}&format=image%2Fpng&LEGEND_OPTIONS=forceLabels:off;dpi:90`;

  try {
    const promises = rules.map(async (rule) => {
      const requestParam = rule.name ? `&rule=${encodeURIComponent(rule.name)}` : '';
      const response = await fetch(`${url}${requestParam}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch legend for rule: ${rule.name}`);
      }

      const blob = await response.blob();
      const label = rule.title?.length > 0 ? rule.title : rule.name;
      return { blob, label };
    });

    const result = await Promise.all(promises);
    return result;
  } catch (error) {
    throw new Error(`Error in fetchWmsLegendItems: ${error}`);
  }
};

const fetchWmsLegend = async (layerName: string, resolution?: number) => {
  const baseurl = `${wmsApiUrl}&Layer=${layerName}`;
  const scaleParam = resolution ? `&scale=${resolutionToScale(resolution)}` : '';

  const request = await fetch(`${baseurl}&format=application/json${scaleParam}`);
  if (!request.ok) {
    throw new Error(`Failed to fetch WMS legend data for layer: ${layerName}`);
  }

  const response = await request.json();
  if (!response.Legend || response.Legend.length === 0) {
    throw new Error(`No legend data found in the response: ${layerName}`);
  }

  const rules = response.Legend[0].rules
    .filter(
      (rule: WmsLegendRule) =>
        !(
          rule.symbolizers &&
          rule.symbolizers.some((sym) => Object.keys(sym)[0] === 'Text')
        ),
    )
    .map((rule: WmsLegendRule) => ({
      name: rule.name,
      title: rule.title,
      symbolizers: rule.symbolizers || [],
    }));

  const result = await fetchWmsLegendItems(baseurl, rules);
  return result;
};

export const useWmsLegendQuery = (layerName: string, resolution?: number) => {
  return useQuery<WmsLegendItem[]>({
    queryKey: ['wmsLegend', layerName, resolution],
    queryFn: () => fetchWmsLegend(layerName, resolution),
    gcTime: 3600000,
    staleTime: Infinity,
  });
};
